import * as yup from 'yup';

import { useLocale } from '../../hooks/useLocale/useLocale';
import { AppMessages } from 'i18n/messages';
import { User } from 'api/users/users.types';
import { PasswordPolicy } from 'api/auth/auth.types';

const DEFAULT_MINIMUM_PASSWORD_LENGTH = 8;

export const useChangePasswordValidation = (user?: User, passwordPolicy?: PasswordPolicy) => {
  const { formatMessage } = useLocale();

  const passwordSchema = yup
    .string()
    .min(
      passwordPolicy?.minimumPasswordLength ?? DEFAULT_MINIMUM_PASSWORD_LENGTH,
      formatMessage({ id: AppMessages['profile.changePassword.validation.length'] }),
    )
    .max(30, formatMessage({ id: AppMessages['profile.changePassword.validation.length'] }))
    .matches(/[a-z]/, formatMessage({ id: AppMessages['profile.changePassword.validation.info.wrongPassword'] }))
    .matches(/[A-Z]/, formatMessage({ id: AppMessages['profile.changePassword.validation.info.wrongPassword'] }))
    .matches(/\d/, formatMessage({ id: AppMessages['profile.changePassword.validation.info.wrongPassword'] }))
    .matches(/[\W_]/, formatMessage({ id: AppMessages['profile.changePassword.validation.info.wrongPassword'] }))
    .test(
      'no-username',
      formatMessage({ id: AppMessages['profile.changePassword.validation.info.wrongPassword'] }),
      (value) => {
        const username = user?.username?.toLowerCase();
        return username ? !value?.toLowerCase()?.includes(username) : true;
      },
    )
    .test(
      'no-firstname',
      formatMessage({ id: AppMessages['profile.changePassword.validation.info.wrongPassword'] }),
      (value) => {
        const firstName = user?.imie?.toLowerCase();
        return firstName ? !value?.toLowerCase()?.includes(firstName) : true;
      },
    )
    .test(
      'no-lastname',
      formatMessage({ id: AppMessages['profile.changePassword.validation.info.wrongPassword'] }),
      (value) => {
        const lastName = user?.nazwisko?.toLowerCase();
        return lastName ? !value?.toLowerCase()?.includes(lastName) : true;
      },
    )
    .required(formatMessage({ id: AppMessages['common.validation.required'] }));

  const schema = yup.object().shape({
    oldPassword: yup.string().required(formatMessage({ id: AppMessages['common.validation.required'] })),
    newPassword: passwordSchema.test(
      'not-same-as-old',
      formatMessage({ id: AppMessages['profile.changePassword.validation.newDifference'] }),
      function (value) {
        const { oldPassword } = this.parent;
        return !oldPassword || !value || value !== oldPassword;
      },
    ),
    repeatPassword: yup
      .string()
      .oneOf(
        [yup.ref('newPassword'), null],
        formatMessage({ id: AppMessages['profile.changePassword.validation.difference'] }),
      )
      .concat(passwordSchema),
  });

  return { schema };
};
